import { CSS } from '@react-spring/web';
import { SystemProp, Theme } from '@xstyled/styled-components';
import { Breakpoints } from '~/lib/useResponsive';
import {
  HORIZONTAL_AUDIO_PLAYER,
  CARD,
  CAROUSEL_NEXT,
  CardDefinition,
  IMAGE,
  ImageDefinition,
  ItemIds,
  LayoutListDefinition,
  LAYOUT_LIST,
  TEXT,
  Alignment,
  VideoDefinition,
  VIDEO,
  CLOSE_CARD
} from '../../items';
import { HorizontalAudioPlayerDefinition } from '../../items/audio-player/HorizontalAudioPlayer';
import { ItemDefinition } from '../../items/Item';
import { ItemHelper } from './types';
import { isIOS, isSafari } from 'react-device-detect';

/**
 * Turns an API Card item into a tree of items with a CARD item as root
 * @param src The API Card item
 * @returns A CARD item with child items
 */
export const CardHelper: ItemHelper<
  'Card' | 'Question',
  CardDefinition,
  {
    width?: CardDefinition['values']['width'];
    height?: CardDefinition['values']['height'];
    padding?: CardDefinition['values']['padding'];
    extraItems?: ItemDefinition<ItemIds>[];
    align?: Alignment;
    clearStyles?: Breakpoints;
    blocking?: boolean;
    replay?: boolean;
    canScroll?: boolean;
    className?: string;
    isImageDecorative?: boolean;
    type?: string;
    extraType?: string;
    minHeight?: string;
    doorId?: string;
    centerTitle?: boolean;
    canWeDeleteCard?: boolean;
    editable?: boolean;
    overflow?: SystemProp<CSS.Property.Overflow, Theme>;
    /** Padding of the inline (card) audio player, e.g. '0px'. Defaults to '16px'. */
    audioPadding?: string;
    /**
     * Vertically center the card content. Useful for growing cards
     * (`height: 'unset'` + `minHeight`) so short content stays centered.
     */
    verticalCenter?: boolean;
    /** Fixed width for the card image (keeps aspect ratio via `imageObjectFit`). */
    imageWidth?: ImageDefinition['values']['width'];
    /** Fixed height for the card image (keeps aspect ratio via `imageObjectFit`). */
    imageHeight?: ImageDefinition['values']['height'];
    /** How the card image fills its box. Defaults to the component default. */
    imageObjectFit?: ImageDefinition['values']['objectFit'];
    /**
     * Cards6 / CAT-1236: enable GIF pause even when the CDN URL has no `.gif`
     * extension (sniff Content-Type / magic bytes; fail open to controls).
     */
    enableAnimationPause?: boolean;
  }
> = (src, options) => {
  const isDoorsCard = options?.className === 'doorsCard';
  const hasCarouselNext = options?.extraItems?.some(
    item => item.type === CAROUSEL_NEXT
  );
  let rootItem: LayoutListDefinition | null = null;
  let videos: VideoDefinition[] = [];

  if (
    (src.__typename === 'Card' || src.__typename === 'Question') &&
    src.videos?.length
  ) {
    videos = src.videos
      .map(v => {
        const mediaUrl =
          isIOS || isSafari
            ? ([v.mediaUrl && { src: v.mediaUrl, type: 'video/mp4' }].filter(
                Boolean
              ) as Array<{ src: string; type: string }>)
            : ([
                v.hlsUrl && {
                  src: v.hlsUrl,
                  type: 'application/vnd.apple.mpegurl'
                },
                v.mediaUrl && { src: v.mediaUrl, type: 'video/mp4' }
              ].filter(Boolean) as Array<{ src: string; type: string }>);

        if (!mediaUrl || mediaUrl?.length === 0) {
          return null;
        }

        return {
          type: VIDEO,
          id: v.id,
          values: {
            videoId: v?.id,
            mediaUrl: mediaUrl,
            autoPlay: v.autoPlay,
            subtitlesUrl: v.subtitlesUrl,
            snapshotUrl: v.snapshotUrl,
            showPlayButton: true,
            isolated: true,
            blocking: options?.blocking,
            loop: v.loop || false,
            replay:
              typeof options?.replay !== undefined ? options?.replay : true,
            videoContainerStyles: {
              width: '100%',
              height: '233px',
              position: 'relative'
            },
            videoRecord: v.videoRecord
          }
        } as VideoDefinition;
      })
      .filter(Boolean) as VideoDefinition[];
  }

  let pictures: ImageDefinition[] = [];

  if (src.pictures?.length) {
    let replay = options && options.replay ? true : false;

    /* in the Interactive30 template, we need to implement replay for audio content */
    /* in the Cards5 template, we need to implement replay for audio content */
    try {
      throw new Error();
    } catch (error) {
      if (error.stack.includes('Interactive30')) {
        replay = true;
      }
    }

    pictures = src.pictures.map(
      (p, i) =>
        ({
          id: p.id,
          type: IMAGE,
          values: {
            imageGenerationPlacement: 'card_image',
            url: p.mediaUrl,
            alt: options?.isImageDecorative ? '' : p.name,
            ...(options?.imageWidth ? { width: options.imageWidth } : {}),
            ...(options?.imageHeight ? { height: options.imageHeight } : {}),
            ...(options?.imageObjectFit
              ? { objectFit: options.imageObjectFit }
              : {}),
            ...(options?.editable === false ? { editable: false } : {}),
            ...(options?.enableAnimationPause
              ? { enableAnimationPause: true }
              : {})
          },
          items: [].filter(Boolean)
        }) as ImageDefinition
    );
  }

  if (pictures.length || videos.length) {
    rootItem = {
      id: `${src.id}.list`,
      type: LAYOUT_LIST,
      // The doors popup card scrolls as a whole (see `.doorsCard`), so the
      // inner lists must grow with their content instead of being clipped by
      // the default `height: 100%` + `overflow: hidden`.
      values: isDoorsCard
        ? { height: 'unset', overflow: 'visible' }
        : hasCarouselNext
          ? { overflow: 'visible' }
          : {},
      items: [...pictures, ...videos]
    };
  }

  const textListType =
    (options && options.extraType) || (options && options.type) || 'div';

  const textList: LayoutListDefinition = {
    id: `${src.id}.text-list`,
    type: LAYOUT_LIST,
    values: {
      padded: true,
      type: textListType,
      align: (options && options.align) || 'center',
      overflow: isDoorsCard
        ? 'visible'
        : hasCarouselNext
          ? 'visible'
          : (options && options.overflow) || 'hidden',
      ...(isDoorsCard ? { height: 'unset' } : {}),
      // The ul/li elements here only exist for their styles and the
      // aria-live behaviour of LayoutList; the children are headings/quiz
      // controls, not list items, so drop the list semantics for AT.
      ...(textListType === 'ul' || textListType === 'li'
        ? { listRole: 'presentation' }
        : {})
      // align: isDoorsCard ? 'left' : (options && options.align) || 'center'
    },
    items: []
  };

  if (src.subtitle) {
    textList.items.push({
      id: `${src.id}_subtitle`,
      type: TEXT,
      values: {
        value: src.subtitle,
        variant: 'sub_heading',
        icon: src?.icon,
        ...(options?.editable === false ? { editable: false } : {})
      },
      items: []
    });
  }

  if (src.title) {
    textList.items.push({
      id: `${src.id}_title`,
      type: TEXT,
      values: {
        value: src.title,
        // variant: 'heading3',
        variant: 'heading1',
        center: options?.centerTitle,
        ...(options?.editable === false ? { editable: false } : {})
      },
      items: []
    });
  }

  if (src.body) {
    textList.items.push({
      id: `${src.id}_body`,
      type: TEXT,
      values: {
        value: src.body,
        canScroll: isDoorsCard ? false : options && options.canScroll,
        padding: isDoorsCard ? '11px' : undefined,
        fontSize: isDoorsCard ? '16px' : undefined,
        ...(options?.editable === false ? { editable: false } : {})
      },
      items: []
    });
  }

  if (src.audios && src.audios.length) {
    const i = 0;

    textList.items.push({
      type: HORIZONTAL_AUDIO_PLAYER,
      id: src.audios[i].id,
      items: [],
      values: {
        mediaUrl: src.audios[i].mediaUrl,
        subtitlesUrl: src.audios[i].subtitlesUrl,
        autoPlay: src.audios[i].autoPlay,
        name: src.audios[i].name,
        blocking: src.__typename === 'Card',
        replay: false,
        parentId: src.id,
        generated: src.audios?.[i]?.generated || false,
        variant: 'card',
        fullBleed: true,
        ...(options?.audioPadding ? { padding: options.audioPadding } : {}),
        ...(options?.editable === false ? { editable: false } : {})
      }
    } as HorizontalAudioPlayerDefinition);
  }

  if (options?.extraItems?.length) {
    textList.items = [...textList.items, ...options.extraItems];
  }

  const isZeroCardPadding = (padding: CardDefinition['values']['padding']) => {
    if (padding === undefined) return true;
    if (padding === 0 || padding === '0' || padding === '0px') return true;
    return false;
  };

  if (rootItem && textList.items.length) {
    rootItem.items.push(textList);
  }

  const closeCardObject = {
    id: `${src.id}.edit-button2`,
    type: CLOSE_CARD,
    values: {
      isSubmit: true,
      text: 'Got it!',
      isBlocked: true,
      cardId: options?.doorId,
      linkedAudioId: undefined
    },
    items: []
  } as ItemDefinition<ItemIds>;

  if (isDoorsCard) {
    if (rootItem) {
      (closeCardObject.values as Record<string, unknown>).linkedAudioId =
        src.audios?.[0]?.id;
      rootItem.items.push(closeCardObject);
    } else {
      textList.items.push(closeCardObject);
    }
  }

  return {
    id: src.id,
    position: src.position || undefined,
    start: src.appear || undefined,
    type: CARD,
    values: {
      ...(options || {}),
      ...(hasCarouselNext && isZeroCardPadding(options?.padding)
        ? { padding: '0 0 8px 0' }
        : {})
    },
    items: [rootItem || textList]
  };
};
